import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Stack, Link, CircularProgress,
} from '@mui/material';
import Logo from '../components/Logo';
import { api } from '../config/api';
import { leerError } from '../utils/errores';

const RestablecerContrasena: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [estado, setEstado] = useState<'validando' | 'valido' | 'invalido'>('validando');
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setEstado('invalido');
      setError('El enlace está incompleto. Ábrelo directamente desde el correo o solicita uno nuevo.');
      return;
    }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then(() => setEstado('valido'))
      .catch((err) => {
        setEstado('invalido');
        setError(leerError(err, 'Este enlace no es válido o ya venció. Solicita uno nuevo.').message);
      });
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres.');
    if (password !== confirmar) return setError('Las contraseñas no coinciden.');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      navigate('/login?reset=ok', { replace: true });
    } catch (err: any) {
      const { message, code } = leerError(err, 'No pudimos actualizar tu contraseña. Intenta de nuevo.');
      setError(message);
      if (code === 'TOKEN_INVALIDO') setEstado('invalido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #075E54, #25D366)' }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Logo size={44} />
            <Typography variant="h5" sx={{ pt: 1 }}>Crea una nueva contraseña</Typography>
          </Stack>

          {estado === 'validando' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          )}

          {estado === 'invalido' && (
            <Stack spacing={2}>
              <Alert severity="warning">{error}</Alert>
              <Button component={RouterLink} to="/recuperar-contrasena" variant="contained">Solicitar un enlace nuevo</Button>
            </Stack>
          )}

          {estado === 'valido' && (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <form onSubmit={submit} noValidate>
                <Stack spacing={2}>
                  <TextField
                    label="Nueva contraseña" type="password" fullWidth required autoFocus autoComplete="new-password"
                    value={password} onChange={(e) => setPassword(e.target.value)} helperText="Mínimo 6 caracteres"
                  />
                  <TextField
                    label="Repite la contraseña" type="password" fullWidth required autoComplete="new-password"
                    value={confirmar} onChange={(e) => setConfirmar(e.target.value)}
                    error={!!confirmar && confirmar !== password}
                    helperText={confirmar && confirmar !== password ? 'Las contraseñas no coinciden' : ' '}
                  />
                  <Button type="submit" variant="contained" size="large" disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar contraseña'}
                  </Button>
                </Stack>
              </form>
            </>
          )}

          <Typography variant="body2" textAlign="center" sx={{ mt: 3 }}>
            <Link component={RouterLink} to="/login">Volver a iniciar sesión</Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RestablecerContrasena;
